import { formatLargeNumber } from './numberFormatter';

export interface CurrencyFormatterOptions {
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact' | 'scientific' | 'engineering';
  compactDisplay?: 'short' | 'long';
  useGrouping?: boolean;
  useCustomAbbreviation?: boolean;
}

export function formatCurrency(
  value: number,
  options: CurrencyFormatterOptions = {}
): string {
  // Handle invalid values
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return '$0';
  }
  
  const {
    currency = 'USD',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    notation = 'standard',
    compactDisplay = 'short',
    useGrouping = true,
    useCustomAbbreviation = false,
  } = options;

  // Use custom abbreviation for compact notation
  if (notation === 'compact' && useCustomAbbreviation) {
    const formattedNumber = formatLargeNumber(value, {
      decimalPlaces: maximumFractionDigits || 1,
      useAbbreviation: true,
      minimumValue: 1000,
    });
    // Add currency symbol
    const currencySymbol = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0).replace(/[\d.,\s]/g, '');
    
    return `${currencySymbol}${formattedNumber}`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    notation,
    compactDisplay,
    useGrouping,
  }).format(value);
}

export function formatCompactCurrency(value: number, currency = 'USD'): string {
  // Handle invalid values
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return '$0';
  }
  
  return formatCurrency(value, {
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
    useCustomAbbreviation: true,
  });
}

export function formatDetailedCurrency(value: number, currency = 'USD'): string {
  // Handle invalid values
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return '$0.00';
  }
  
  return formatCurrency(value, {
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}